import { Badge } from '@ipfabric/ui-components/Badge/Badge';
import {
  type SnapshotId,
  type TableColumn,
  isNonNullable,
} from '@ipfabric/utils';
import type {
  TestCloudResourceTagSiteSeparationRuleRequestBody,
  TestRegexSiteSeparationCloudBasedRuleRequestBody,
  TestRegexSiteSeparationRuleRequestBody,
  TestRegexSiteSeparationRuleResponseBody,
} from '@ipfabric/utils/schema/siteSeparation/siteSeparation';
import type { SiteSeparationAttributeTransformation } from '@ipfabric/utils/schema/siteSeparation/siteSeparationAttributeTransformation';
import type { SiteSeparationRule } from '@ipfabric/utils/schema/siteSeparation/siteSeparationRule';
import {
  createEnumColumn,
  createTextColumn,
} from '@ipfabric/utils/table/columns';
import { pick } from '@ipfabric/utils/utils/pick';
import type { ReactNode } from 'react';

export type TestRuleTab = 'matched' | 'unmatched';
export type TestableRuleType = Extract<
  SiteSeparationRule['type'],
  | 'regexHostname'
  | 'regexSnmpLocation'
  | 'regexCloudResourceId'
  | 'cloudResourceTag'
>;

type TestRuleSharedValues = {
  transformation: SiteSeparationAttributeTransformation;
  applyToCloudInstances: boolean;
};

export type TestRuleValues =
  | ({
      type: Extract<
        TestableRuleType,
        'regexHostname' | 'regexSnmpLocation' | 'regexCloudResourceId'
      >;
      regex: string;
      siteName: string | null;
    } & TestRuleSharedValues)
  | ({
      type: 'cloudResourceTag';
      tagNames: string[];
    } & TestRuleSharedValues);

/**
 * Flat RHF shape. `Control<TestRuleValues>` cannot name-index union-only
 * fields, so the form stays joined and conversion happens at the boundary.
 */
export type TestRuleFormValues = Omit<
  Extract<TestRuleValues, { regex: string }>,
  'type'
> &
  Omit<Extract<TestRuleValues, { type: 'cloudResourceTag' }>, 'type'> & {
    type: TestableRuleType;
  };

const CLOUD_RESOURCE_TAG_TEST_KEYS = [
  'type',
  'tagNames',
  'transformation',
  'applyToCloudInstances',
] as const;

const REGEX_TEST_RULE_KEYS = [
  'type',
  'regex',
  'siteName',
  'transformation',
  'applyToCloudInstances',
] as const;

export const toTestRuleFormValues = (
  values: TestRuleValues,
): TestRuleFormValues => ({
  regex: '',
  siteName: null,
  tagNames: [],
  ...values,
});

export const toTestRuleValues = (values: TestRuleFormValues): TestRuleValues =>
  values.type === 'cloudResourceTag'
    ? { ...pick(CLOUD_RESOURCE_TAG_TEST_KEYS)(values), type: values.type }
    : { ...pick(REGEX_TEST_RULE_KEYS)(values), type: values.type };

export type TestRuleResourceType = 'onPremise' | 'cloud';

export type TestRuleTableRow = {
  id: string;
  hostname: string;
  snmpLocation: string;
  resourceType: TestRuleResourceType;
  siteName: string;
};

type TestRuleTabConfig = {
  badge: ReactNode;
  id: TestRuleTab;
  label: string;
};

const toMatchedRow = (
  row: TestRegexSiteSeparationRuleResponseBody['matched'][number],
): TestRuleTableRow => ({
  id: `device-${row.vDeviceId}`,
  hostname: row.hostname,
  snmpLocation: row.snmpLocation ?? '-',
  resourceType: 'onPremise',
  siteName: row.siteName,
});

const toUnmatchedRow = (
  row: TestRegexSiteSeparationRuleResponseBody['unmatched'][number],
): TestRuleTableRow => ({
  id: `device-${row.vDeviceId}`,
  hostname: row.hostname,
  snmpLocation: row.snmpLocation ?? '-',
  resourceType: 'onPremise',
  siteName: '-',
});

const toMatchedCloudNetworkRow = (
  row: NonNullable<
    TestRegexSiteSeparationRuleResponseBody['matchedCloudNetworks']
  >[number],
): TestRuleTableRow => ({
  id: `cloudNetwork-${row.vCloudNetworkId}`,
  hostname: row.resourceName,
  snmpLocation: '-',
  resourceType: 'cloud',
  siteName: row.siteName,
});

const toUnmatchedCloudNetworkRow = (
  row: NonNullable<
    TestRegexSiteSeparationRuleResponseBody['unmatchedCloudNetworks']
  >[number],
): TestRuleTableRow => ({
  id: `cloudNetwork-${row.vCloudNetworkId}`,
  hostname: row.resourceName,
  snmpLocation: '-',
  resourceType: 'cloud',
  siteName: '-',
});

export const getTestRulePayload = (
  values: TestRuleValues,
  snapshotId: SnapshotId | null,
):
  | TestRegexSiteSeparationRuleRequestBody
  | TestRegexSiteSeparationCloudBasedRuleRequestBody
  | TestCloudResourceTagSiteSeparationRuleRequestBody => {
  const snapshot = snapshotId ?? undefined;

  if (values.type === 'cloudResourceTag') {
    return { ...pick(CLOUD_RESOURCE_TAG_TEST_KEYS)(values), snapshot };
  }

  const base = {
    ...pick(['type', 'regex', 'siteName', 'transformation'] as const)(values),
    snapshot,
  };

  if (values.type === 'regexCloudResourceId') {
    return { ...base, applyToCloudInstances: values.applyToCloudInstances };
  }

  return base;
};

export const getTestRuleTableData = (
  result: TestRegexSiteSeparationRuleResponseBody | null,
  activeTab: TestRuleTab,
): TestRuleTableRow[] => {
  if (!result) {
    return [];
  }

  if (activeTab === 'matched') {
    return [
      ...result.matched.map(toMatchedRow),
      ...(result.matchedCloudNetworks ?? []).map(toMatchedCloudNetworkRow),
    ];
  }

  return [
    ...result.unmatched.map(toUnmatchedRow),
    ...(result.unmatchedCloudNetworks ?? []).map(toUnmatchedCloudNetworkRow),
  ];
};

export const getTestRuleTableColumns = (
  showSnmpLocation: boolean,
  showResourceType: boolean,
  activeTab: TestRuleTab,
): TableColumn<TestRuleTableRow>[] => {
  const columns: TableColumn<TestRuleTableRow>[] = [
    createTextColumn({
      key: 'hostname',
      title: 'Hostname / Resource ID',
    }),
  ];

  if (showSnmpLocation) {
    columns.push(
      createTextColumn({
        key: 'snmpLocation',
        title: 'SNMP location',
      }),
    );
  }

  if (showResourceType) {
    columns.push(
      createEnumColumn({
        key: 'resourceType',
        title: 'Type',
        width: 110,
        options: {
          onPremise: 'On-premise',
          cloud: 'Cloud',
        },
      }),
    );
  }

  if (activeTab === 'matched') {
    columns.push(
      createTextColumn({
        key: 'siteName',
        title: 'Site name',
      }),
    );
  }

  return columns;
};

export const getTestRuleTabs = (
  result: TestRegexSiteSeparationRuleResponseBody | null,
): TestRuleTabConfig[] => [
  {
    badge: isNonNullable(result) && (
      <Badge color='neutral' variant='plain'>
        {result.matched.length + (result.matchedCloudNetworks ?? []).length}
      </Badge>
    ),
    id: 'matched',
    label: 'Matched',
  },
  {
    badge: isNonNullable(result) && (
      <Badge color='neutral' variant='plain'>
        {result.unmatched.length + (result.unmatchedCloudNetworks ?? []).length}
      </Badge>
    ),
    id: 'unmatched',
    label: 'Unmatched',
  },
];
